import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
// @mui
import {
  Box,
  Card,
  Grid,
  Stack,
  Table,
  Rating,
  Divider,
  Container,
  TextField,
  TableBody,
  Typography,
  TableCell,
  TableRow,
  InputAdornment,
  LinearProgress,
  TableContainer,
  TablePagination,
} from '@mui/material';
// components
import Page from 'src/components/Page';
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
import Scrollbar from 'src/components/Scrollbar';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import {
  TableNoData,
  ColumnsButton,
  TableSkeleton,
  TableHeadCustom,
} from 'src/components/table';
import useTable, { getComparator } from 'src/hooks/useTable';
import useColumnVisibility, { ColumnDef } from 'src/hooks/useColumnVisibility';
import { PATH_DASHBOARD } from 'src/routes/paths';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';
import { fDateTimeSuffix } from 'src/utils/formatTime';

// ----------------------------------------------------------------------

const STUDENT_COLUMNS: ColumnDef[] = [
  { id: 'name', label: 'Student', align: 'left' },
  { id: 'email', label: 'Email', align: 'left' },
  { id: 'phone', label: 'Phone', align: 'left' },
  { id: 'progress', label: 'Progress', align: 'center' },
  { id: 'attendance', label: 'Attendance', align: 'center' },
  { id: 'quiz', label: 'Quiz', align: 'center' },
  { id: 'final', label: 'Final score', align: 'center' },
  { id: 'status', label: 'Status', align: 'center' },
];

function StatCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card sx={{ p: 3, height: '100%' }}>
      <Typography variant="subtitle2" color="text.secondary" noWrap>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1 }}>
        {value}
      </Typography>
      {sub ? (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      ) : null}
    </Card>
  );
}

// ----------------------------------------------------------------------

export default function CourseReport() {
  const { id } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [students, setStudents] = useState<any[]>([]);
  const [ratings, setRatings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [filterName, setFilterName] = useState('');

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangePage,
    onChangeRowsPerPage,
  } = useTable({ defaultOrderBy: 'final', defaultOrder: 'desc' });

  const { visibleColumns, toggleColumn, headLabel } = useColumnVisibility(
    STUDENT_COLUMNS,
    'kayfa.courseReport.visibleColumns'
  );

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axiosInstance.get(APIS.ADMIN_STATS.COURSE_REPORT(id as string));
        setData(res.data?.data ?? res.data);
      } catch (e: any) {
        enqueueSnackbar(e?.message || 'Failed to load course report', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    })();
    (async () => {
      setLoadingStudents(true);
      try {
        const res = await axiosInstance.get(APIS.ADMIN_STATS.COURSE_STUDENTS(id as string));
        const payload = res.data?.data ?? res.data;
        setStudents(Array.isArray(payload?.items) ? payload.items : []);
      } catch (e: any) {
        enqueueSnackbar(e?.message || 'Failed to load students', { variant: 'error' });
      } finally {
        setLoadingStudents(false);
      }
    })();
    // Course ratings + comments for THIS course (filtered from the all-courses
    // feed) — shown inline in the report instead of a separate page.
    (async () => {
      try {
        const res = await axiosInstance.get(APIS.ADMIN_STATS.COURSE_RATINGS);
        const payload = res.data?.data ?? res.data;
        const items = Array.isArray(payload?.items) ? payload.items : [];
        setRatings(items.filter((r: any) => String(r.courseId) === String(id)));
      } catch {
        setRatings([]);
      }
    })();
  }, [id, enqueueSnackbar]);

  const watchHours = data ? Math.round((data.watch?.watchedMinutes || 0) / 60) : 0;

  // Client-side sort + search (course rosters are modest in size).
  const sortKey = (s: any) => ({
    name: s.name,
    email: s.email,
    phone: s.phone,
    progress: s.progress,
    attendance: s.attendanceScore,
    quiz: s.quizScore,
    final: s.finalScore,
    status: s.finalScore,
  });
  const rows = [...students]
    .map((s) => ({ ...s, ...sortKey(s) }))
    .sort(getComparator(order, orderBy))
    .filter(
      (s) =>
        !filterName ||
        s.name?.toLowerCase().includes(filterName.toLowerCase()) ||
        s.email?.toLowerCase().includes(filterName.toLowerCase())
    );
  const paged = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  const isNotFound = !rows.length && !!filterName;
  const isVisible = (k: string) => visibleColumns.includes(k);

  return (
    <Page title="Course Report">
      <Container maxWidth="lg">
        <HeaderBreadcrumbs
          heading={data?.course?.title ? `Report — ${data.course.title}` : 'Course Report'}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Courses', href: PATH_DASHBOARD.courses.root },
            { name: 'Report' },
          ]}
        />

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {data && (
          <Grid container spacing={3} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Enrolled" value={data.enrolled} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Completed"
                value={data.completed}
                sub={`${data.completionRate}% completion`}
              />
            </Grid>
            {data.score && (
              <Grid item xs={12} sm={6} md={3}>
                <StatCard
                  label="Final score"
                  value={`${data.score.finalScore}%`}
                  sub={`pass ≥ ${data.score.threshold}%`}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Avg quiz score"
                value={`${data.quiz?.avgPercentage || 0}%`}
                sub={`${data.quiz?.passRate || 0}% pass · ${data.quiz?.attempts || 0} attempts`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                label="Avg rating"
                value={data.rating?.count ? `${data.rating.avg}/5` : '—'}
                sub={`${data.rating?.count || 0} ratings`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Certificate-eligible" value={data.certificateEligible} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Watch time" value={`${watchHours}h`} sub={`${data.watch?.viewers || 0} viewers`} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard label="Quizzes" value={data.quiz?.quizzes || 0} />
            </Grid>
          </Grid>
        )}

        {/* Students in this course */}
        <Card>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ p: 2, gap: 1.5, flexWrap: 'wrap' }}
          >
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Students ({rows.length})
            </Typography>
            <TextField
              size="small"
              value={filterName}
              onChange={(e) => {
                setFilterName(e.target.value);
                setPage(0);
              }}
              placeholder="Search student..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: 220 }}
            />
            <ColumnsButton
              columns={STUDENT_COLUMNS}
              visibleColumns={visibleColumns}
              onToggle={toggleColumn}
            />
          </Stack>

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={headLabel}
                  rowCount={rows.length}
                  onSort={onSort}
                />
                <TableBody>
                  {loadingStudents ? (
                    [...Array(5)].map((_, i) => <TableSkeleton key={`cs-skeleton-${i}`} />)
                  ) : (
                    <>
                      {paged.map((s) => (
                        <TableRow key={s.userId} hover>
                          {isVisible('name') && (
                            <TableCell>
                              <Typography variant="subtitle2" noWrap>
                                {s.name || '—'}
                              </Typography>
                            </TableCell>
                          )}
                          {isVisible('email') && <TableCell>{s.email || '—'}</TableCell>}
                          {isVisible('phone') && <TableCell>{s.phone || '—'}</TableCell>}
                          {isVisible('progress') && (
                            <TableCell align="center">{s.progress}%</TableCell>
                          )}
                          {isVisible('attendance') && (
                            <TableCell align="center">{s.attendanceScore}%</TableCell>
                          )}
                          {isVisible('quiz') && (
                            <TableCell align="center">
                              {s.quizScore}%
                              {s.quizAttempts ? (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  {s.quizAttempts} attempt{s.quizAttempts > 1 ? 's' : ''}
                                </Typography>
                              ) : null}
                            </TableCell>
                          )}
                          {isVisible('final') && (
                            <TableCell align="center">
                              <Typography variant="subtitle2">{s.finalScore}%</Typography>
                            </TableCell>
                          )}
                          {isVisible('status') && (
                            <TableCell align="center">
                              <Label color={s.eligible ? 'success' : 'default'}>
                                {s.completed ? 'Completed' : s.eligible ? 'Eligible' : 'In progress'}
                              </Label>
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                      <TableNoData isNotFound={isNotFound} />
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rows.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={onChangePage}
              onRowsPerPageChange={onChangeRowsPerPage}
            />
          </Box>
        </Card>

        {/* Course ratings + comments (this course only) — inline, not a tab */}
        <Card sx={{ mt: 3 }}>
          <Stack direction="row" alignItems="center" sx={{ p: 2, gap: 1 }}>
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              Course ratings ({ratings.length})
            </Typography>
            {data?.rating?.count ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Rating value={Number(data.rating.avg) || 0} precision={0.1} size="small" readOnly />
                <Typography variant="body2">{data.rating.avg}/5</Typography>
              </Stack>
            ) : null}
          </Stack>
          <Divider />
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                {ratings.length === 0 ? (
                  <TableRow>
                    <TableCell align="center" sx={{ py: 4 }} colSpan={3}>
                      No ratings yet for this course.
                    </TableCell>
                  </TableRow>
                ) : (
                  ratings.map((r, i) => (
                    <TableRow key={`${r.userId}-${i}`} hover>
                      <TableCell sx={{ width: 240 }}>
                        <Stack spacing={0.25}>
                          <Typography variant="subtitle2" noWrap>
                            {r.studentName || '—'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {r.studentEmail || ''}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ width: 140 }}>
                        <Rating value={Number(r.rating) || 0} size="small" readOnly />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{r.comment || '—'}</Typography>
                        {r.date ? (
                          <Typography variant="caption" color="text.secondary">
                            {fDateTimeSuffix(new Date(r.date))}
                          </Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Container>
    </Page>
  );
}
